import { UserButton, SignedIn, SignedOut } from "@clerk/clerk-react";
import { Coins, Menu, Settings as SettingsIcon, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { startAnonymousTrial } from "@/lib/demo";

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const isClerkConfigured = clerkPubKey && !clerkPubKey.includes("YOUR_CLERK");

export default function Header({ minimal = false, tier = null, creditBalance = 0 } = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [nudgeSignup, setNudgeSignup] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handler = () => {
      setNudgeSignup(true);
      setTimeout(() => setNudgeSignup(false), 2000);
    };
    window.addEventListener("flexlearn:nudge-signup", handler);
    return () => window.removeEventListener("flexlearn:nudge-signup", handler);
  }, []);
  const goToAnonymousTrial = () => {
    startAnonymousTrial();
    navigate("/dashboard");
  };
  const goToUpgrade = () => {
    navigate("/pricing");
  };
  const goToReview = async () => {
    try {
      const { api } = await import("@/lib/api");
      const response = await api.getDueForReview();
      const firstDue = (response.data || [])[0];
      if (firstDue?.book_id) {
        navigate(`/flashcards/${firstDue.book_id}`);
        return;
      }
    } catch {
      // Dashboard still gives the user their library and review badges.
    }
    navigate("/dashboard");
  };

  const AuthedNavLinks = (
    <>
      <Link
        to="/dashboard"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-dashboard"
      >
        {t("header.dashboard")}
      </Link>
      <Link
        to="/upload"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-upload"
      >
        {t("header.uploadBook")}
      </Link>
      <Link
        to="/progress"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-progress"
      >
        {t("header.progress")}
      </Link>
      <button
        type="button"
        onClick={goToReview}
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-review"
      >
        {t("header.review")}
      </button>
      <Link
        to="/guides"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-guides"
      >
        {t("header.guides")}
      </Link>
      <Link
        to="/contact"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-contact"
      >
        {t("header.contact")}
      </Link>
    </>
  );

  const PricingNavLink = ({ to }) => (
    <Link
      to={to}
      className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
      data-testid="nav-pricing"
    >
      {t("header.pricing")}
    </Link>
  );

  const MarketingNavLinks = (
    <>
      <Link
        to="/guides"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-guides"
      >
        {t("header.guides")}
      </Link>
      <Link
        to="/#how-it-works"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-how-it-works"
      >
        {t("header.howItWorks")}
      </Link>
      <PricingNavLink to="/#pricing" />
      <Link
        to="/about"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-about"
      >
        {t("header.about")}
      </Link>
      <Link
        to="/contact"
        className="text-muted-foreground hover:text-terracotta transition-colors font-medium"
        data-testid="nav-contact"
      >
        {t("header.contact")}
      </Link>
    </>
  );

  const NavLinks = () =>
    isClerkConfigured ? (
      <>
        <SignedIn>
          {AuthedNavLinks}
          <PricingNavLink to="/pricing" />
        </SignedIn>
        <SignedOut>{MarketingNavLinks}</SignedOut>
      </>
    ) : (
      <>
        {AuthedNavLinks}
        <PricingNavLink to="/pricing" />
      </>
    );

  if (minimal) {
    return (
      <header className="glass-header fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <span className="font-heading font-black text-xl leading-none">
              <span className="text-secondary">Flex</span>
              <span className="text-terracotta">LearnAI</span>
            </span>
            <img
              src="/logo-small.v1.webp"
              alt="FlexLearnAI"
              className="h-11 w-auto"
              width="44"
              height="44"
              decoding="async"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/logo-small.png";
              }}
            />
          </Link>
          <div className="flex items-center gap-2">
            {isClerkConfigured ? (
              <SignedIn>
                <Link
                  to="/settings"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/80 text-card-foreground hover:bg-accent hover:text-terracotta"
                  data-testid="settings-link"
                  aria-label={t("header.settings")}
                >
                  <SettingsIcon className="h-4 w-4" />
                </Link>
              </SignedIn>
            ) : (
              <Link
                to="/settings"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/80 text-card-foreground hover:bg-accent hover:text-terracotta"
                data-testid="settings-link"
                aria-label={t("header.settings")}
              >
                <SettingsIcon className="h-4 w-4" />
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="h-11 text-muted-foreground hover:text-terracotta"
              data-testid="exit-quiz-btn"
            >
              <X className="w-5 h-5 mr-1" />
              {t("header.exit")}
            </Button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)]">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo">
            <span className="font-heading font-black text-xl leading-none">
              <span className="text-secondary">Flex</span>
              <span className="text-terracotta">LearnAI</span>
            </span>
            <img
              src="/logo-small.v1.webp"
              alt="FlexLearnAI"
              className="h-10 w-auto"
              width="40"
              height="40"
              decoding="async"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/logo-small.png";
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-4">
            {isClerkConfigured ? (
              <SignedIn>
                <Link
                  to="/settings"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/80 text-card-foreground hover:bg-accent hover:text-terracotta"
                  data-testid="settings-link"
                  aria-label={t("header.settings")}
                >
                  <SettingsIcon className="h-4 w-4" />
                </Link>
              </SignedIn>
            ) : (
              <Link
                to="/settings"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/80 text-card-foreground hover:bg-accent hover:text-terracotta"
                data-testid="settings-link"
                aria-label={t("header.settings")}
              >
                <SettingsIcon className="h-4 w-4" />
              </Link>
            )}
            {isClerkConfigured ? (
              <>
                <SignedIn>
                  <NavLinks />
                  {creditBalance > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-900">
                      <Coins className="h-3.5 w-3.5" />
                      {t("header.creditsBadge", { count: creditBalance })}
                    </span>
                  )}
                  {tier === "free" && (
                    <Button
                      onClick={goToUpgrade}
                      className="rounded-full border border-sky-200 bg-sky-100 px-5 text-sky-700 hover:bg-sky-200"
                      data-testid="get-plus-btn"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t("header.getPlus")}
                    </Button>
                  )}
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: "w-10 h-10",
                      },
                    }}
                  />
                </SignedIn>
                <SignedOut>
                  <NavLinks />
                  <Link to="/sign-in">
                    <Button
                      variant="ghost"
                      className="text-muted-foreground hover:text-terracotta"
                      data-testid="sign-in-btn"
                    >
                      {t("header.signIn")}
                    </Button>
                  </Link>
                  <Link to="/sign-up">
                    <Button
                      className={`bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-6${nudgeSignup ? " animate-header-cta" : ""}`}
                      data-testid="get-started-btn"
                    >
                      {t("header.getStarted")}
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={goToAnonymousTrial}
                    className="rounded-full px-6 border-2 border-border hover:border-terracotta hover:text-terracotta"
                    data-testid="trial-dashboard-btn"
                  >
                    {t("header.tryFree")}
                  </Button>
                </SignedOut>
              </>
            ) : (
              <>
                <NavLinks />
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-6"
                  data-testid="demo-dashboard-btn"
                >
                  {t("header.demoMode")}
                </Button>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="xl:hidden pt-4 pb-2 flex flex-col gap-4 animate-fade-in">
            {isClerkConfigured ? (
              <SignedIn>
                <Link
                  to="/settings"
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-card-foreground hover:bg-accent self-start"
                  data-testid="mobile-settings-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <SettingsIcon className="h-4 w-4" />
                  <span>{t("header.settings")}</span>
                </Link>
              </SignedIn>
            ) : (
              <Link
                to="/settings"
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-card-foreground hover:bg-accent self-start"
                data-testid="mobile-settings-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SettingsIcon className="h-4 w-4" />
                <span>{t("header.settings")}</span>
              </Link>
            )}
            <NavLinks />
            {isClerkConfigured ? (
              <>
                <SignedIn>
                  {creditBalance > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-900 self-start">
                      <Coins className="h-3.5 w-3.5" />
                      {t("header.creditsBadge", { count: creditBalance })}
                    </span>
                  )}
                  {tier === "free" && (
                    <Button
                      onClick={goToUpgrade}
                      className="w-full rounded-full border border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-200"
                      data-testid="mobile-get-plus-btn"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t("header.getPlus")}
                    </Button>
                  )}
                  <div className="pt-2">
                    <UserButton afterSignOutUrl="/" />
                  </div>
                </SignedIn>
                <SignedOut>
                  <Link to="/sign-in">
                    <Button variant="ghost" className="w-full justify-start">
                      {t("header.signIn")}
                    </Button>
                  </Link>
                  <Link to="/sign-up">
                    <Button
                      className={`w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-full${nudgeSignup ? " animate-header-cta" : ""}`}
                    >
                      {t("header.getStarted")}
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={goToAnonymousTrial}
                    className="w-full rounded-full border-2 border-border hover:border-terracotta hover:text-terracotta"
                    data-testid="mobile-trial-btn"
                  >
                    {t("header.tryFree")}
                  </Button>
                </SignedOut>
              </>
            ) : (
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-full"
              >
                {t("header.demoMode")}
              </Button>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
